import { Box, Col } from '@opendoor/atoms/box';
import { IconBrandGooglePlay } from '@tabler/icons-react';

import { TOUR_SHEET_COPY } from './tourBookingSheetCopy';

const APPLE_STORE_LINK = 'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';

/** Scanned on desktop; the link lands the phone on its store. Matches the two-QR handoff modal's get-app QR target. */
export const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';

export const buildConfirmationAttributedUrl = (
  baseUrl: string,
  content: string,
  addressToken?: string | null,
) => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', 'marketplace_pdp');
  url.searchParams.set('utm_medium', 'tour_booking_confirmation');
  url.searchParams.set('utm_content', content);
  if (addressToken) {
    url.searchParams.set('utm_term', addressToken);
  }
  return url.toString();
};

const AppleLogo = () => (
  <svg viewBox="0 0 20 24" width="20" height="24" className="shrink-0" aria-hidden>
    <path
      transform="translate(0 5.789)"
      fill="currentColor"
      d="M16.705 6.974C16.717 6.054 16.967 5.152 17.432 4.352C17.897 3.552 18.562 2.880 19.365 2.398C18.855 1.687 18.182 1.102 17.400 0.689C16.618 0.276 15.748 0.047 14.859 0.020C12.964 -0.175 11.126 1.127 10.160 1.127C9.175 1.127 7.688 0.039 6.086 0.071C5.050 0.104 4.041 0.398 3.156 0.925C2.271 1.452 1.541 2.193 1.037 3.078C-1.146 6.768 0.482 12.192 2.573 15.175C3.620 16.636 4.843 18.267 6.443 18.209C8.009 18.146 8.593 17.234 10.484 17.234C12.356 17.234 12.905 18.209 14.537 18.173C16.218 18.146 17.276 16.705 18.286 15.231C19.038 14.190 19.616 13.040 20.000 11.822C19.024 11.419 18.191 10.745 17.605 9.882C17.019 9.020 16.706 8.009 16.705 6.974Z"
    />
    <path
      transform="translate(9.956 0)"
      fill="currentColor"
      d="M3.665 3.847C4.581 2.773 5.032 1.393 4.923 0.000C3.524 0.144 2.231 0.797 1.302 1.829C0.848 2.334 0.501 2.920 0.279 3.556C0.057 4.192 -0.034 4.864 0.011 5.534C0.711 5.541 1.404 5.393 2.037 5.101C2.670 4.808 3.226 4.380 3.665 3.847Z"
    />
  </svg>
);

const GooglePlayLogo = () => <IconBrandGooglePlay size={22} className="shrink-0" aria-hidden />;

const STORES = {
  apple: {
    href: APPLE_STORE_LINK,
    analyticsName: 'self-tour-booking-app-store-badge-confirmation',
    copy: TOUR_SHEET_COPY.confirmation.appStore,
    Logo: AppleLogo,
  },
  googlePlay: {
    href: GOOGLE_PLAY_LINK,
    analyticsName: 'self-tour-booking-google-play-badge-confirmation',
    copy: TOUR_SHEET_COPY.confirmation.googlePlay,
    Logo: GooglePlayLogo,
  },
} as const;

const isAndroid = () => typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

/**
 * The confirmation step's mobile store badge, pointed at the buyer's own
 * store. Attribution is this surface's own (utm_medium=tour_booking_confirmation),
 * so clicks in Journey stay separable from the two-QR handoff modal's.
 */
export const AppStoreButton = ({ addressToken }: { addressToken?: string | null }) => {
  const { href, analyticsName, copy, Logo } = isAndroid() ? STORES.googlePlay : STORES.apple;
  return (
    <Box
      href={buildConfirmationAttributedUrl(href, 'confirmation', addressToken)}
      external
      analyticsName={analyticsName}
      aria-label={copy.label}
      trackImpression
      className="w-full items-center justify-center gap-2 overflow-clip rounded-md bg-black px-2 py-3 text-white"
    >
      <Logo />
      <Col className="shrink-0 items-start">
        <span className="font-['SF_Compact_Text',system-ui,sans-serif] text-[9px] leading-[9px] font-medium">
          {copy.kicker}
        </span>
        <span className="font-['SF_Compact_Display',system-ui,sans-serif] text-[18px] leading-none font-medium tracking-[-0.47px]">
          {copy.name}
        </span>
      </Col>
    </Box>
  );
};
